import { useState, useEffect, useMemo, useCallback } from "react";
import { DateRange } from "react-day-picker";
import { SignedInUser, NestedUser, NestedCommission } from "@revelate/types";
import useSWR from "swr";
import { getCommissions } from "@/lib/supabase/commissions";
import { get, TableType } from "@/lib/supabase";

interface UseCommissionsDataProps {
  currentUser: SignedInUser | null;
  dateRange?: DateRange;
  userIds?: string[];
  enabled?: boolean;
}

interface CommissionsDataState {
  users: NestedUser[];
  error: string | null;
  isLoading: boolean;
}

/**
 * Hook to fetch commissions data directly from the database
 * This replaces the calculation-heavy useCommissionsStream hook
 */
export function useCommissionsData({
  currentUser,
  dateRange,
  userIds,
  enabled = true,
}: UseCommissionsDataProps) {
  const [state, setState] = useState<CommissionsDataState>({
    users: [],
    error: null,
    isLoading: false,
  });

  // Fetch company data with users
  const {
    data: company,
    error: companyError,
    isLoading: companyLoading,
  } = useSWR(
    enabled && currentUser
      ? ["companies" as TableType, currentUser.company_id?.toString()]
      : null,
    ([table, companyId]) => get(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Create a key for the commissions data
  const commissionsKey = enabled && userIds && userIds.length > 0 && dateRange
    ? ["commissions", JSON.stringify(userIds), JSON.stringify(dateRange)]
    : null;

  // Fetch commissions data
  const {
    data: commissions,
    error: commissionsError,
    isLoading: commissionsLoading,
    mutate: mutateCommissions,
  } = useSWR(
    commissionsKey,
    async () => {
      if (!userIds || !dateRange) return [];
      return getCommissions(userIds, dateRange);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 10_000, // ms
    }
  );

  // Process the data when it's available
  useEffect(() => {
    // Handle loading state
    if (companyLoading || commissionsLoading) {
      setState(prev => ({ ...prev, isLoading: true }));
      return;
    }

    // Handle errors
    if (companyError) {
      setState({
        users: [],
        error: companyError.message,
        isLoading: false,
      });
      return;
    }

    if (commissionsError) {
      setState({
        users: [],
        error: commissionsError.message,
        isLoading: false,
      });
      return;
    }

    // Process data when available
    if (company && commissions) {
      // Use a callback to process the data to avoid creating functions in the render cycle
      const processData = () => {
        // Create a map of user IDs to users
        const userMap = new Map<string, NestedUser>();
        
        // Initialize users from company data
        if (company.users) {
          company.users.forEach((user: NestedUser) => {
            // Only filter by active status and include_in_calculations
            if ((!userIds || userIds.includes(user.id)) && 
                user.include_in_calculations && 
                user.is_active !== false) {
              // Create a copy of the user without plans to reduce payload size
              const userWithoutPlans = {
                ...user,
                commissions: [],
                plans: [], // Empty the plans array to reduce payload size
                goals: [], // Also empty goals to further reduce payload
                teams: [], // Also empty teams to further reduce payload
              };
              
              userMap.set(user.id, userWithoutPlans);
            }
          });
        }

        // Group commissions by user
        if (commissions) {
          commissions.forEach(commission => {
            const userId = commission.user_id;
            if (userId && userMap.has(userId)) {
              const user: NestedUser | undefined = userMap.get(userId);
              if (user) {
                user.commissions.push(commission);
              }
            }
          });
        }

        // Sort commissions by rate in ascending order within each user
        userMap.forEach((user: NestedUser) => {
          user.commissions.sort((a, b) => (a.rate || 0) - (b.rate || 0));
        });

        return Array.from(userMap.values());
      };

      // Process the data and update state
      const processedUsers = processData();
      setState({
        users: processedUsers,
        error: null,
        isLoading: false,
      });
    }
  }, [company, commissions, companyLoading, commissionsLoading, companyError, commissionsError, userIds]);

  // Function to update commissions in the cache - wrapped in useCallback to prevent unnecessary re-renders
  const mutate = useCallback(async (updatedCommissions?: NestedCommission[]) => {
    if (!updatedCommissions || !commissions) return;

    // Create a map of existing commissions by ID
    const commissionsMap = new Map(commissions.map(c => [c.id, c]));

    // Update the map with the new commissions
    updatedCommissions.forEach(commission => {
      if (commission.id) {
        commissionsMap.set(commission.id, commission);
      }
    });

    // Convert back to array
    const updatedCommissionsArray = Array.from(commissionsMap.values());

    // Update the SWR cache
    await mutateCommissions(updatedCommissionsArray, false);
  }, [commissions, mutateCommissions]);

  // Memoize the return value to prevent unnecessary re-renders
  const returnValue = useMemo(() => ({
    users: state.users,
    error: state.error,
    isLoading: state.isLoading || companyLoading || commissionsLoading,
    mutate,
  }), [state.users, state.error, state.isLoading, companyLoading, commissionsLoading, mutate]);

  return returnValue;
}
